<script lang="ts">
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import { orderStore } from "/@/stores/OrderStore"
  import { selectedOrder } from "/@/stores/SelectedOrderStore"
  import PanelTopBarLayout from "/@lib/components/PanelTopBarLayout.svelte"
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import ClickableTable from "/@lib/components/table/ClickableTable.svelte"
  import { TABLE_STORAGE_KEYS } from "/@lib/components/table/column-layout"
  import { twApiClient } from "/@lib/tastyworks-rest"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import {
    formatPosEff,
    formatReceivedTime,
    signForOrderAction,
  } from "/@lib/utils/formats"
  import {
    CustomerAccountRowResult,
    ItemsResponse,
    OrderStatus,
    OrderStatusLeg,
    UrlPathBuilder,
    type Pagination,
  } from "@tastyworks/tastyworks-api"
  import { Button, ButtonGroup, Dialog, Input } from "@tastyworks/ui-library"
  import { debounce } from "lodash-es"
  import { Temporal } from "temporal-polyfill"
  import VanillaContextMenu from "vanilla-context-menu"

  const ORDER_STATUS_OPTIONS = ["Working", "Filled", "Cancelled", "Rejected"]

  const DAYS_OPTIONS = [
    { label: "Today", value: 0 },
    { label: "Yesterday", value: 1 },
    { label: "7 Days", value: 7 },
  ]

  const ORDER_EVENT_COLUMNS: ColumnDefinitions = [
    {
      name: "Event",
      dataFormatter: (event: { eventValue: string }) => event.eventValue,
    },
    {
      name: "Occurred At",
      dataFormatter: (event: { occurredAt: string }) =>
        formatReceivedTime(event.occurredAt),
    },
  ]

  const ORDERS_COLUMNS: ColumnDefinitions = [
    {
      name: "Order #",
      dataFormatter: (order: OrderStatus) => order.id,
      linkFormatter: (order: OrderStatus) =>
        new UrlPathBuilder(import.meta.env.VITE_TW_BASE_URL)
          .nested("admin")
          .nested("order_api_orders")
          .toUrl(order.id),
      width: 6,
    },
    {
      name: "Received At",
      dataFormatter: (order: OrderStatus) =>
        formatReceivedTime(order.receivedAt),
      width: 10,
      comparator: (order: OrderStatus) => new Date(order.receivedAt),
    },
    {
      name: "status",
      dataFormatter: (order: OrderStatus) => order.status,
      popupTableFormatter: {
        fetchItemInfo: (order: OrderStatus) =>
          twApiClient.AdminOrderEventsService.getOrderEvents(
            order.accountNumber,
            order.id
          ),
        columns: ORDER_EVENT_COLUMNS,
      },
      width: 7,
    },
    {
      name: "Symbol",
      dataFormatter: (order: OrderStatus) => order.underlyingSymbol,
      width: 4,
    },
    {
      name: "Size",
      dataFormatter: (order: OrderStatus) =>
        (order.legs[0] ? signForOrderAction(order.legs[0].action) : "") +
        order.size,
      width: 3,
      comparator: (order: OrderStatus) =>
        Number(
          (order.legs[0] ? signForOrderAction(order.legs[0].action) : "") +
            order.size
        ),
      style: "text-right",
    },
    {
      name: "Price",
      dataFormatter: (order: OrderStatus) => order.price || "--",
      width: 4,
      comparator: (order: OrderStatus) =>
        order.price ? Number(order.price) : Number.NEGATIVE_INFINITY,
      style: "text-right",
    },
    {
      name: "TIF",
      dataFormatter: (order: OrderStatus) => order.timeInForce,
      width: 3,
    },
    {
      name: "Username",
      dataFormatter: (order: OrderStatus) => order.username,
      width: 10,
    },
  ]

  const LEG_COLUMNS: ColumnDefinitions = [
    {
      name: "Pos Eff",
      dataFormatter: (leg: OrderStatusLeg) => formatPosEff(leg.action),
    },
    {
      name: "Qty",
      dataFormatter: (leg: OrderStatusLeg) =>
        signForOrderAction(leg.action) + leg.quantity,
      comparator: (leg: OrderStatusLeg) =>
        Number(signForOrderAction(leg.action) + leg.quantity),
      style: "text-right",
    },
    {
      name: "Description",
      dataFormatter: (leg: OrderStatusLeg) => leg.symbol,
    },
  ]

  const WORKING_STATES = [
    "Received",
    "Routed",
    "In Flight",
    "Live",
    "Cancel Requested",
    "Replace Requested",
  ]

  let orderPromise: Promise<ItemsResponse<OrderStatus> | null> = $state(
    Promise.resolve(null)
  )
  let days: number | null = $state($orderStore.days)
  let orderStatus: string[] = $state([])
  let orderLegs: OrderStatusLeg[] = $state([])
  let menu: null | VanillaContextMenu = $state(null)
  let cancelOrderOpen = $state(false)

  const openDialog = () => {
    cancelOrderOpen = true
  }

  const cleanUpAndFetch = (
    selectedCustomerAccount: CustomerAccountRowResult
  ) => {
    if (selectedCustomerAccount.account) {
      orderLegs = []
      orderPromise = searchOrders()
    }
  }

  const searchOrders = (offset?: number) => {
    if (!$selectedCustomerAccount.account) {
      return Promise.reject(new Error("No account selected"))
    }

    return twApiClient.AdminOrdersService.index(
      $selectedCustomerAccount.account?.accountNumber,
      {
        endDate: $orderStore.endDate || undefined,
        pageOffset: offset,
        perPage: 200,
        receivedAt: $orderStore.receivedAt || undefined,
        startDate: $orderStore.startDate || undefined,
        status: $orderStore.status || undefined,
        underlyingSymbol:
          $orderStore.underlyingSymbol.toUpperCase() || undefined,
      }
    )
  }

  const handleStatuses = (orderStatus: string[]) => {
    if (orderStatus.includes("Working")) {
      $orderStore.status = orderStatus.concat(WORKING_STATES)
    } else {
      $orderStore.status = orderStatus
    }
  }

  const handleDateFilter = (days: number | null) => {
    if (days === null) {
      $orderStore.startDate = ""
      $orderStore.endDate = ""
    } else {
      $orderStore.startDate = Temporal.Now.plainDateISO()
        .subtract(new Temporal.Duration(0, 0, 0, days))
        .toString()
      $orderStore.endDate = Temporal.Now.plainDateISO().toString()
    }
  }

  function cancelOrder() {
    if ($selectedCustomerAccount.account && $selectedOrder.cancellable) {
      twApiClient.AdminOrdersService.cancel(
        $selectedCustomerAccount.account.accountNumber,
        $selectedOrder.id
      ).then(() => {
        cleanUpAndFetch($selectedCustomerAccount)
      })
    }
  }

  const updateMenu = () => {
    menu = new VanillaContextMenu({
      scope: document.activeElement as HTMLElement,
      menuItems: [
        {
          label: "Cancel Order",
          callback: () => {
            if ($selectedOrder.cancellable) {
              openDialog()
            }
          },
          preventCloseOnClick: !$selectedOrder.cancellable,
        },
      ],
      transitionDuration: 0,
      theme: "white",
      customClass: "context-menu",
      customThemeClass: $selectedOrder.cancellable ? "" : "context-menu",
    })
  }

  const disableMenu = () => {
    if (menu && !orderPromise) {
      menu.off()
    }
  }

  $effect(() => {
    disableMenu()
  })

  // Two-way sync: store → local picks up external resets (e.g. the
  // Clear button firing orderStore.reset() while this tab is mounted);
  // local → store persists the override across tab unmount/remount.
  $effect(() => {
    days = $orderStore.days
  })

  $effect(() => {
    handleDateFilter(days)
  })

  $effect(() => {
    $orderStore.days = days
  })

  $effect(() => {
    handleStatuses(orderStatus)
  })

  // Read filter fields explicitly so the effect re-runs when they change;
  // reads inside the nested searchOrders() call aren't tracked reliably.
  // underlyingSymbol is omitted because the Symbol input already calls
  // cleanUpAndFetch via a debounced on:input handler.
  $effect(() => {
    void $orderStore.startDate
    void $orderStore.endDate
    void $orderStore.status
    cleanUpAndFetch($selectedCustomerAccount)
  })
</script>

<div
  role="button"
  class="flex grow flex-col"
  tabindex={0}
  onkeydown={(e) => {
    e.key === "Tab" && menu?.close()
  }}
>
  <PanelTopBarLayout>
    <Button
      variant="secondary"
      onclick={() => {
        cleanUpAndFetch($selectedCustomerAccount)
      }}>Refresh</Button
    >

    <Input
      type="search"
      placeholder="Symbol"
      class="max-w-36 min-w-19 bg-white uppercase"
      on:input={debounce(() => cleanUpAndFetch($selectedCustomerAccount), 300)}
      bind:value={$orderStore.underlyingSymbol}
    />
    <Dialog.Root bind:open={cancelOrderOpen}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content>
          <div class="flex justify-between text-xl">
            {"Cancel Order ID: " + $selectedOrder.id}
            <Dialog.Close />
          </div>
          <p>Are you sure you want to do this?</p>
          <div class="flex w-full flex-row justify-center gap-3">
            <Dialog.Close variant="secondary">No</Dialog.Close>
            <Dialog.Close
              name="submit"
              type="submit"
              variant="secondary"
              onclick={() => {
                cancelOrder()
              }}>Yes</Dialog.Close
            >
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  </PanelTopBarLayout>
  <PanelTopBarLayout>
    <ButtonGroup.Root
      variant="default"
      aria-label="working filled toggle"
      deselect={true}
      type="multiple"
      bind:value={orderStatus}
    >
      {#each ORDER_STATUS_OPTIONS as option}
        <ButtonGroup.Item value={option} aria-label={option}>
          {option}
        </ButtonGroup.Item>
      {/each}
    </ButtonGroup.Root>

    <ButtonGroup.Root
      variant="default"
      aria-label="date toggle"
      deselect={true}
      bind:value={days}
    >
      {#each DAYS_OPTIONS as option}
        <ButtonGroup.Item
          value={option.value}
          aria-label={option.label}
          aria-pressed={days === option.value}
        >
          {option.label}
        </ButtonGroup.Item>
      {/each}
    </ButtonGroup.Root>
  </PanelTopBarLayout>

  <div class="relative flex h-full flex-col border-2" id="order-table">
    <AwaitItemsResponse
      promise={orderPromise}
      noResultsMessage="No Notes Found"
    >
      {#snippet children({
        items,
        pagination,
        isLastPage,
      }: {
        items: OrderStatus[]
        pagination: Pagination
        isLastPage: boolean
      })}
        <div class="flex h-0 grow flex-col">
          <ClickableTable
            storageKey={TABLE_STORAGE_KEYS.orders}
            class="basis-3/4"
            columns={ORDERS_COLUMNS}
            {items}
            onItemSelected={(item) => {
              orderLegs = (item as OrderStatus).legs
              $selectedOrder = item as OrderStatus
              updateMenu()
            }}
          />
          <div class="flex justify-between">
            <Button
              variant="secondary"
              disabled={pagination.pageOffset === 0}
              onclick={() => {
                orderPromise = searchOrders(pagination.pageOffset - 1)
              }}
            >
              &lt;&lt;
            </Button>

            <Button
              variant="secondary"
              disabled={isLastPage}
              onclick={() => {
                orderPromise = searchOrders(
                  pagination.nextPaginationParams.pageOffset
                )
              }}
            >
              &gt;&gt;
            </Button>
          </div>
          <div class="relative flex h-0 grow basis-1/3">
            <ClickableTable
              storageKey={TABLE_STORAGE_KEYS.orderLegs}
              columns={LEG_COLUMNS}
              items={orderLegs}
              fillWidth
            />
          </div>
        </div>
      {/snippet}
    </AwaitItemsResponse>
  </div>
</div>

<style lang="postcss">
  @reference "@tastyworks/style-config/variables.css";

  :global(.context-menu > *) {
    @apply bg-white text-gray-400;
  }
</style>
